import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarBlank,
  CheckCircle,
  ClipboardText,
  Clock,
  FileText,
  Phone,
  Target,
  TrendUp,
  User,
  UsersThree,
  WarningCircle,
} from "@phosphor-icons/react";

import DashboardBusinessGrid, { DashboardPriorityAlert } from "@/components/dashboard/DashboardBusinessGrid";

const QUOTES_ROUTE = "/app/commerce/quotes";

function numberValue(value) {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatNumber(value) {
  return new Intl.NumberFormat("fr-FR").format(numberValue(value));
}

function formatCurrency(value) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(numberValue(value));
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function dateValue(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function localDateKey(value) {
  const date = dateValue(value);
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function formatTime(value) {
  const date = dateValue(value);
  if (!date) return "—";
  return date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatTodayLabel() {
  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date());
}

function periodLabel(period = {}) {
  if (period.preset === "today") return "Aujourd’hui";
  if (period.preset === "this-month") return "Ce mois";
  if (period.preset === "previous-month") return "Mois précédent";
  if (period.preset === "last-3-months") return "3 mois";
  if (period.preset === "last-6-months") return "6 derniers mois";
  if (period.preset === "current-year") return "Année en cours";
  if (period.preset === "custom") return "Personnalisée";
  return "6 derniers mois";
}

function toInputDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function addMonths(value, amount) {
  const date = new Date(value);
  date.setMonth(date.getMonth() + amount);
  return date;
}

function startOfMonth(value) {
  return new Date(value.getFullYear(), value.getMonth(), 1);
}

function endOfMonth(value) {
  return new Date(value.getFullYear(), value.getMonth() + 1, 0);
}

function quickPeriod(key) {
  const today = new Date();
  const todayKey = toInputDate(today);

  if (key === "today") {
    return { mode: "exact", from: todayKey, to: todayKey, preset: key };
  }

  if (key === "this-month") {
    return {
      mode: "between",
      from: toInputDate(startOfMonth(today)),
      to: toInputDate(endOfMonth(today)),
      preset: key,
    };
  }

  if (key === "previous-month") {
    const previous = addMonths(today, -1);
    return {
      mode: "between",
      from: toInputDate(startOfMonth(previous)),
      to: toInputDate(endOfMonth(previous)),
      preset: key,
    };
  }

  if (key === "last-3-months") {
    return {
      mode: "between",
      from: toInputDate(addMonths(today, -3)),
      to: todayKey,
      preset: key,
    };
  }

  if (key === "current-year") {
    return {
      mode: "between",
      from: `${today.getFullYear()}-01-01`,
      to: `${today.getFullYear()}-12-31`,
      preset: key,
    };
  }

  return {
    mode: "between",
    from: toInputDate(addMonths(today, -6)),
    to: todayKey,
    preset: "last-6-months",
  };
}

function actionLabel(type) {
  return {
    meeting: "Rendez-vous",
    call: "Appel",
    email: "Email",
    followup: "Relance",
    other: "Tâche",
  }[type] || "Tâche";
}

function prospectTitle(item = {}) {
  return item.company
    || item.prospectName
    || `${item.firstName || ""} ${item.lastName || ""}`.trim()
    || "Prospect";
}

function isOpenAction(item = {}) {
  if (!item.nextAction || !item.nextActionAt) return false;
  return !item.nextActionStatus || item.nextActionStatus === "open";
}

function isToday(item) {
  return localDateKey(item?.nextActionAt) === localDateKey(new Date());
}

function isOverdue(item) {
  const date = dateValue(item?.nextActionAt);
  return isOpenAction(item) && date && date.getTime() < Date.now() && !isToday(item);
}

function ActionIcon({ type }) {
  const Icon = type === "meeting"
    ? CalendarBlank
    : type === "followup" || type === "call"
      ? Phone
      : ClipboardText;

  return <Icon size={18} weight="duotone" />;
}

function MetricCard({ title, value, detail, icon: Icon, tone = "primary", to }) {
  const toneClass = {
    primary: "text-[var(--do-accent)] bg-[var(--do-accent-soft)]",
    violet: "text-violet-600 bg-violet-50",
    success: "text-emerald-600 bg-emerald-50",
    orange: "text-orange-600 bg-orange-50",
  }[tone] || "text-[var(--do-accent)] bg-[var(--do-accent-soft)]";

  const content = (
    <article className="rounded-2xl border bg-[var(--do-bg-solid)] p-4 shadow-sm" style={{ borderColor: "var(--do-border)" }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-black text-[var(--do-muted)]">{title}</div>
          <div className="mt-2 text-[26px] font-black tracking-tight text-[var(--do-ink)]">{value}</div>
          <div className="mt-1 text-xs leading-4 text-[var(--do-muted)]">{detail}</div>
        </div>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${toneClass}`}>
          <Icon size={18} weight="duotone" />
        </span>
      </div>
    </article>
  );

  return to ? <Link to={to} className="block">{content}</Link> : content;
}

function SectionHeader({ title, count, action, to }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <h2 className="text-[14px] font-black uppercase tracking-[0.04em] text-[var(--do-ink)]">{title}</h2>
        {count !== undefined && (
          <span className="rounded-full bg-[var(--do-accent-soft)] px-2 py-0.5 text-[10px] font-black text-[var(--do-accent)]">{count}</span>
        )}
      </div>
      {action && to && <Link to={to} className="shrink-0 text-xs font-black text-[var(--do-accent)]">{action}</Link>}
    </div>
  );
}

export default function MobileCommercialHomeV2({
  dashboard,
  period,
  onPeriodChange,
  canFilterTeam,
  ownerUserId,
  onOwnerUserIdChange,
  commercialUsers,
  currentUser,
  agendaRows,
}) {
  const canTeamView = Boolean(canFilterTeam && currentUser?.role !== "user");
  const [teamView, setTeamView] = useState(false);
  const [periodOpen, setPeriodOpen] = useState(false);

  useEffect(() => {
    if (!canTeamView) return;

    // Ce composant reste monté derrière lg:hidden sur Desktop.
    // Il ne doit pas modifier le filtre commercial global
    // lorsqu'il n'est pas réellement affiché.
    const isMobileViewport = window.matchMedia(
      "(max-width: 1023px)"
    ).matches;

    if (!isMobileViewport) return;

    let savedTeamView = false;
    try {
      savedTeamView = window.localStorage.getItem("acta.dashboard.mobile.teamView") === "true";
    } catch {
      savedTeamView = false;
    }

    setTeamView(savedTeamView);
    onOwnerUserIdChange(savedTeamView ? "" : String(currentUser?.id || ""));
  }, [canTeamView, currentUser?.id, onOwnerUserIdChange]);

  const toggleTeamView = (next) => {
    setTeamView(next);
    onOwnerUserIdChange(next ? "" : String(currentUser?.id || ""));
    try {
      window.localStorage.setItem("acta.dashboard.mobile.teamView", String(next));
    } catch {
      // Optional preference only.
    }
  };

  const selectedOwner = String(ownerUserId || "");

  const scopedAgenda = useMemo(() => {
    const rows = safeArray(agendaRows).filter((item) => isOpenAction(item) || item?.nextActionType === "meeting");
    if (!canTeamView || !selectedOwner) return rows;
    return rows.filter((item) => String(item?.assignedUserId || "") === selectedOwner);
  }, [agendaRows, canTeamView, selectedOwner]);

  const todayRows = useMemo(() => (
    scopedAgenda
      .filter(isToday)
      .sort((a, b) => new Date(a.nextActionAt).getTime() - new Date(b.nextActionAt).getTime())
      .slice(0, 6)
  ), [scopedAgenda]);

  const overdueRows = useMemo(() => (
    scopedAgenda
      .filter(isOverdue)
      .sort((a, b) => new Date(a.nextActionAt).getTime() - new Date(b.nextActionAt).getTime())
  ), [scopedAgenda]);

  // Conserver une seule liste opérationnelle dans la carte Agenda (mode personnel).
  const mobileAgendaItems = useMemo(() => {
    const byId = new Map();
    [...overdueRows, ...todayRows].forEach((row) => {
      const id = String(row.nextActionNoteId || row.id || row.prospectId || "");
      const key = id + ":" + String(row.nextActionAt || "") + ":" + String(row.nextAction || "");
      if (!byId.has(key)) byId.set(key, row);
    });
    return Array.from(byId.values()).slice(0, 6);
  }, [overdueRows, todayRows]);

  const todayTasks = todayRows.filter((row) => row.nextActionType !== "meeting");
  const todayMeetings = todayRows.filter((row) => row.nextActionType === "meeting");

  const commercial = dashboard?.commercial || {};
  const quotes = dashboard?.quotes || {};
  const financial = dashboard?.financialPeriod || {};
  const metrics = financial?.metrics || {};
  const pipeline = financial?.pipeline || commercial?.pipeline || {};

  const opportunities = numberValue(metrics.forecastCount ?? commercial.openOpportunityCount);
  const potentialRevenue = numberValue(metrics.forecastAmount ?? commercial.forecastAmount);
  const realizedRevenue = numberValue(metrics.confirmedRevenueAmount ?? commercial.confirmedRevenueAmount ?? commercial.wonAmount);
  const wonCount = numberValue(pipeline?.won?.count);
  const lostCount = numberValue(pipeline?.lost?.count);
  const closedCount = wonCount + lostCount;

  const overdueCount = numberValue(commercial.overdueFollowupsCount) || overdueRows.length;
  const quotePending = numberValue(quotes.toTreat) || numberValue(quotes.pendingValidation) + numberValue(quotes.approvedToSend);
  const inactiveBusinessCount = numberValue(commercial.closingSoonCount) || safeArray(commercial.closingSoon).length;
  const alertRows = [
    { label: "Relances en retard", value: overdueCount, icon: WarningCircle, to: "/app/commerce/agenda" },
    { label: "Affaires à surveiller", value: inactiveBusinessCount, icon: Clock, to: "/app/commerce/pipeline" },
    { label: "Devis en attente", value: quotePending, icon: FileText, to: QUOTES_ROUTE },
  ].filter((row) => row.value > 0).slice(0, 3);

  const teamRows = useMemo(() => {
    if (!teamView || !canTeamView) return [];

    const byOwner = new Map();
    safeArray(agendaRows).forEach((row) => {
      const ownerId = String(row?.assignedUserId || "");
      if (!ownerId) return;

      const current = byOwner.get(ownerId) || {
        ownerId,
        name: row.ownerName || "Commercial",
        tasks: 0,
        overdue: 0,
      };

      current.tasks += 1;
      if (isOverdue(row)) current.overdue += 1;
      current.name = row.ownerName || current.name;
      byOwner.set(ownerId, current);
    });

    return Array.from(byOwner.values())
      .sort((a, b) => (b.tasks - a.tasks) || (b.overdue - a.overdue))
      .slice(0, 6);
  }, [agendaRows, canTeamView, teamView]);

  const quickPeriods = [
    ["today", "Aujourd’hui"],
    ["this-month", "Ce mois"],
    ["previous-month", "Mois précédent"],
    ["last-3-months", "3 mois"],
    ["last-6-months", "6 mois"],
    ["current-year", "Année"],
  ];

  return (
    <section className="space-y-4 py-3" data-testid="mobile-commercial-dashboard-v3">
      {canTeamView && (
        <div className="grid grid-cols-2 rounded-2xl border bg-[var(--do-bg-solid)] p-1" style={{ borderColor: "var(--do-border)" }} data-testid="mobile-dashboard-scope-toggle">
          <button
            type="button"
            onClick={() => toggleTeamView(false)}
            className={`flex min-h-11 items-center justify-center gap-2 rounded-xl text-sm font-black transition ${!teamView ? "bg-[var(--do-accent-strong)] text-[var(--do-on-accent)] shadow-sm" : "text-[var(--do-muted)]"}`}
            aria-pressed={!teamView}
          >
            <User size={17} weight="duotone" />
            Moi
          </button>
          <button
            type="button"
            onClick={() => toggleTeamView(true)}
            className={`flex min-h-11 items-center justify-center gap-2 rounded-xl text-sm font-black transition ${teamView ? "bg-[var(--do-accent-strong)] text-[var(--do-on-accent)] shadow-sm" : "text-[var(--do-muted)]"}`}
            aria-pressed={teamView}
          >
            <UsersThree size={17} weight="duotone" />
            Mon équipe
          </button>
        </div>
      )}

      {teamView && canTeamView && (
        <label className="flex min-h-11 items-center gap-3 rounded-2xl border bg-[var(--do-bg-solid)] px-3" style={{ borderColor: "var(--do-border)" }}>
          <UsersThree size={18} weight="duotone" className="text-[var(--do-accent)]" />
          <span className="min-w-0 flex-1">
            <span className="block text-[10px] font-black uppercase tracking-wide text-[var(--do-muted)]">Périmètre</span>
            <select
              value={selectedOwner}
              onChange={(event) => onOwnerUserIdChange(event.target.value)}
              className="w-full bg-transparent text-sm font-black text-[var(--do-ink)] outline-none"
              data-testid="mobile-dashboard-commercial-selector"
            >
              <option value="">{currentUser?.role === "manager" ? "Mon équipe" : "Tous les commerciaux"}</option>
              {safeArray(commercialUsers).map((entry) => (
                <option key={entry.id} value={String(entry.id)}>{entry.name}</option>
              ))}
            </select>
          </span>
        </label>
      )}

      {teamView && canTeamView && (
      <section className="rounded-2xl border bg-[var(--do-bg-solid)] shadow-sm" style={{ borderColor: "var(--do-border)" }}>
        <div className="flex items-start justify-between gap-3 p-4 pb-3">
          <div>
            <div className="text-[14px] font-black uppercase tracking-[0.04em] text-[var(--do-ink)]">Aujourd’hui · {formatTodayLabel()}</div>
            <div className="mt-1 text-xs text-[var(--do-muted)]">
              {formatNumber(todayMeetings.length)} rendez-vous · {formatNumber(todayTasks.length)} tâches · {formatNumber(overdueRows.length)} en retard
            </div>
          </div>
          <Link to="/app/commerce/agenda" className="shrink-0 rounded-xl border px-3 py-2 text-xs font-black text-[var(--do-accent)]" style={{ borderColor: "var(--do-border)" }}>
            Voir agenda
          </Link>
        </div>

        <div className="divide-y" style={{ borderColor: "var(--do-border)" }}>
          {todayRows.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-[var(--do-muted)]">Aucun rendez-vous ou tâche prévu aujourd’hui.</div>
          ) : todayRows.map((row, index) => (
            <Link key={`${row.id || row.nextActionNoteId || "action"}-${index}`} to={`/app/commerce/prospects/${encodeURIComponent(row.prospectId || row.id || "")}`} className="flex items-center gap-3 px-4 py-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[var(--do-accent-soft)] text-[var(--do-accent)]">
                <ActionIcon type={row.nextActionType} />
              </span>
              <span className="w-12 shrink-0 text-xs font-black text-[var(--do-accent)]">{formatTime(row.nextActionAt)}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[10px] font-black uppercase text-[var(--do-muted)]">{actionLabel(row.nextActionType)}</span>
                <span className="block truncate text-sm font-black text-[var(--do-ink)]">{prospectTitle(row)}</span>
                <span className="block truncate text-xs text-[var(--do-muted)]">{row.nextAction || "Action commerciale"}</span>
              </span>
              <ArrowRight size={16} weight="bold" className="shrink-0 text-[var(--do-muted)]" />
            </Link>
          ))}
        </div>
      </section>

      )}

      {teamView && canTeamView && (
        <section className="rounded-2xl border bg-[var(--do-bg-solid)] p-4 shadow-sm" style={{ borderColor: "var(--do-border)" }}>
          <SectionHeader title="À surveiller aujourd’hui" action="Voir tout" to="/app/commerce/agenda" />
          <div className="mt-3 grid grid-cols-3 gap-2">
            {alertRows.length === 0 ? (
              <div className="col-span-3 rounded-xl bg-[var(--do-bg-2)] p-4 text-center text-sm text-[var(--do-muted)]">Aucune alerte importante.</div>
            ) : alertRows.map((row) => {
              const Icon = row.icon;
              return <Link key={row.label} to={row.to} className="rounded-xl border p-3" style={{ borderColor: "var(--do-border)" }}><Icon size={18} weight="duotone" className="text-[var(--do-accent)]" /><strong className="mt-2 block text-xl text-[var(--do-ink)]">{formatNumber(row.value)}</strong><span className="mt-1 block text-[10px] leading-4 text-[var(--do-muted)]">{row.label}</span></Link>;
            })}
          </div>
        </section>
      )}

      {teamView && canTeamView && (
        <section className="rounded-2xl border bg-[var(--do-bg-solid)] shadow-sm" style={{ borderColor: "var(--do-border)" }}>
          <div className="p-4 pb-2"><SectionHeader title="Agenda équipe aujourd’hui" action="Voir agenda" to="/app/commerce/agenda" /></div>
          <div className="divide-y" style={{ borderColor: "var(--do-border)" }}>
            {todayRows.length === 0 ? <div className="px-4 py-7 text-center text-sm text-[var(--do-muted)]">Aucun rendez-vous aujourd’hui.</div> : todayRows.slice(0, 6).map((row, index) => (
              <Link key={`team-${row.id || index}`} to={`/app/commerce/prospects/${encodeURIComponent(row.prospectId || row.id || "")}`} className="flex items-center gap-3 px-4 py-3">
                <span className="w-12 shrink-0 text-xs font-black text-[var(--do-accent)]">{formatTime(row.nextActionAt)}</span>
                <span className="min-w-0 flex-1"><span className="block truncate text-sm font-black text-[var(--do-ink)]">{prospectTitle(row)}</span><span className="block truncate text-xs text-[var(--do-muted)]">{row.nextAction || actionLabel(row.nextActionType)}</span></span>
                <span className="max-w-[95px] truncate text-xs font-black text-[var(--do-muted)]">{row.ownerName || "Commercial"}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {!teamView && (
              <section>
                <div className="flex items-center justify-between gap-3 px-0.5">
                  <h2 className="text-[14px] font-black uppercase tracking-[0.04em] text-[var(--do-ink)]">Performance{teamView ? " équipe" : ""}</h2>
                  <div className="relative">
                    <button type="button" onClick={() => setPeriodOpen((value) => !value)} className="inline-flex items-center gap-2 rounded-xl border bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-ink)]" style={{ borderColor: "var(--do-border)" }} aria-expanded={periodOpen} data-testid="mobile-dashboard-performance-period">
                      {periodLabel(period)}
                      <TrendUp size={14} weight="bold" />
                    </button>
                    {periodOpen && (
                      <div className="absolute right-0 top-11 z-30 w-[220px] rounded-2xl border bg-[var(--do-bg-solid)] p-2 shadow-xl" style={{ borderColor: "var(--do-border)" }}>
                        {quickPeriods.map(([key, label]) => (
                          <button key={key} type="button" onClick={() => { onPeriodChange(quickPeriod(key)); setPeriodOpen(false); }} className="block w-full rounded-xl px-3 py-2.5 text-left text-xs font-black text-[var(--do-ink)] hover:bg-[var(--do-bg-2)]">{label}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <MetricCard title="Opportunités" value={formatNumber(opportunities)} detail="Ouvertes sur la période" icon={Target} tone="primary" to="/app/commerce/pipeline" />
                  <MetricCard title="CA potentiel" value={formatCurrency(potentialRevenue)} detail="Opportunités ouvertes" icon={TrendUp} tone="violet" to="/app/commerce/pipeline" />
                  <MetricCard title="CA réalisé" value={formatCurrency(realizedRevenue)} detail="Confirmé sur la période" icon={CheckCircle} tone="success" to="/app/commerce/pipeline" />
                  <MetricCard title="Affaires clôturées" value={formatNumber(closedCount)} detail={`${formatNumber(wonCount)} gagnée(s) · ${formatNumber(lostCount)} perdue(s)`} icon={FileText} tone="orange" />
                </div>
        </section>
      )}

      {!teamView && (
        <div className="space-y-3" data-testid="mobile-dashboard-business-blocks">
          <DashboardPriorityAlert overdueFollowups={commercial.overdueFollowupsCount} />
          <DashboardBusinessGrid dashboard={dashboard} todayMeetings={todayMeetings.length} agendaItems={mobileAgendaItems} />
        </div>
      )}

      {teamView && canTeamView && (
        <section className="rounded-2xl border bg-[var(--do-bg-solid)] p-4 shadow-sm" style={{ borderColor: "var(--do-border)" }}>
          <SectionHeader title="Activité des commerciaux" action="Voir tout" to="/app/commerce/contacts" />
          <div className="mt-3 divide-y" style={{ borderColor: "var(--do-border)" }}>
            {teamRows.length === 0 ? <div className="py-5 text-center text-sm text-[var(--do-muted)]">Aucune activité planifiée aujourd’hui.</div> : teamRows.map((row) => <button key={row.ownerId} type="button" onClick={() => onOwnerUserIdChange(row.ownerId)} className="flex w-full items-center gap-3 py-3 text-left"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--do-accent-soft)] text-[var(--do-accent)]"><User size={17} weight="duotone" /></span><span className="min-w-0 flex-1"><span className="block truncate text-sm font-black text-[var(--do-ink)]">{row.name}</span><span className="block text-xs text-[var(--do-muted)]">{row.tasks} tâche(s) · {row.overdue} retard(s)</span></span><ArrowRight size={15} weight="bold" className="shrink-0 text-[var(--do-muted)]" /></button>)}
          </div>
        </section>
      )}
    </section>
  );
}
